import Delegate from 'ftdomdelegate'

import Dom from '../tools/dom'
import Accessibility from '../tools/accessibility'
import Responsive from '../tools/responsive';

export default class Popover {
  constructor (element, options) {

    this.element = element;
    this.delegateElement = new Delegate(this.element);

    this.activator = options['activator'] || document.querySelector('[aria-controls="' + element.getAttribute('id') + '"]');
    this.preferredPosition = options['preferredPosition'] || 'bottom';
    this.isOpen = false;

    this.onValueChanged = options['onValueChanged'] || function () {};
    this.onOpen = options['onOpen'] || function () {};
    this.onClose = options['onClose'] || function () {};

    this.showOverlay = options['showOverlay'] === undefined ? true : options['showOverlay'];
    this.pageOverlayElement = document.querySelector('.pageOverlay');

    this._attachListeners();
  }

  destroy() {
    this.element.removeEventListener('keyup', this._handleKeyboardListener);
    this.delegateElement.off('click');
    this.activator.removeEventListener('click', this._toggleListener);
  }
  toggle() {
    this.isOpen ? this.close() : this.open();
  }
  open() {
    // Note: the additional check on the aria-controls is used here so that a given activator can be used on different
    //       popovers and be modified dynamically in JavaScript
    if (this.isOpen || this.activator.getAttribute('aria-controls') !== this.element.id) {
      return;
    }

    this.element.setAttribute('aria-hidden', 'false');
    this.activator.setAttribute('aria-expanded', 'true');

    Accessibility.trapFocus(this.element, 'popover');
    // disableBodyScroll(true, '[data-scrollable]');

    document.documentElement.classList.add('no-scroll'); // Prevent scrolling when popover is open

    if (Responsive.matchesBreakpoint('lg')) {
      document.body.addEventListener('click', this._clickOutsideListener);
      this._position();
    } else {
      this.element.removeAttribute('style');

      if (this.showOverlay) {
        this.pageOverlayElement.classList.add('is-visible');
        this.pageOverlayElement.addEventListener('click', this._closeListener);
      }
    }

    this.onOpen(this); // Call the callback to allow other code to hook their logic
    this.isOpen = true;
  }
  close() {
    if (!this.isOpen) {
      return;
    }

    this.element.setAttribute('aria-hidden', 'true');
    this.activator.setAttribute('aria-expanded', 'false');

    Accessibility.removeTrapFocus(this.element, 'popover');
    // disableBodyScroll(false, '[data-scrollable]');

    document.documentElement.classList.remove('no-scroll');

    if (Responsive.matchesBreakpoint('lg')) {
      document.body.removeEventListener('click', this._clickOutsideListener);
    } else if (this.showOverlay) {
      this.pageOverlayElement.classList.remove('is-visible');
      this.pageOverlayElement.removeEventListener('click', this._closeListener);
    }

    this.onClose(this); // Call the callback to allow other code to hook their logic
    this.isOpen = false;
  }
  _attachListeners() {
    this._handleKeyboardListener = this._handleKeyboard.bind(this);
    this._clickOutsideListener = this._clickOutside.bind(this);
    this._closeListener = this.close.bind(this);
    this._toggleListener = this.toggle.bind(this);

    this.element.addEventListener('keyup', this._handleKeyboardListener);
    this.activator.addEventListener('click', this._toggleListener);

    this.delegateElement.on('click', '[data-action="close-popover"]', this.close.bind(this));
    this.delegateElement.on('click', '[data-action="select-value"]', this._valueChanged.bind(this));
  }

  /**
   * Whenever a value is selected, it can notify a callback so that the calling code can do its own logic in response
   * of the value change
   */

  _valueChanged(event) {
    Dom.getSiblings(event.target, '.is-selected').forEach(function (item) {
      return item.classList.remove('is-selected');
    });
    event.target.classList.add('is-selected');

    // If there is a callback in option we call it with the value
    this.onValueChanged(event.target.getAttribute('data-value'), event.target, this.activator);
    this.close();
  }

  /**
   * Callback that is called to decide if we should close the popover when a click is captured outside
   */

  _clickOutside(event) {
    if (!event.target.closest('.popover') && !event.target.closest('.modal') && event.target !== this.activator && !this.activator.contains(event.target)) {
      this.close();
    }
  }

  /**
   * On desktop, we reposition the popover in JavaScript by doing some smart logic to detect the most appropriate area
   */

  _position() {
    var _this4 = this;

    var topPosition = 0,
        rightPosition = 0,
        position = '',
        alignment = '',
        threshold = 20;

    fastdom.measure(function () {
      var windowHeight = window.innerHeight,
          activatorBoundingRect = _this4.activator.getBoundingClientRect(),
          halfHeight = windowHeight / 2;

      if (_this4.preferredPosition === 'bottom') {
        alignment = 'right';

        if (_this4.element.clientHeight <= windowHeight - (activatorBoundingRect.bottom + threshold) || windowHeight - activatorBoundingRect.bottom >= halfHeight) {
          position = 'bottom';
        } else {
          position = 'top';
        }
      } else if (_this4.preferredPosition === 'top') {
        alignment = 'right';

        if (_this4.element.clientHeight <= activatorBoundingRect.top - threshold || activatorBoundingRect.top >= halfHeight) {
          position = 'top';
        } else {
          position = 'bottom';
        }
      } else {
        position = 'left';

        // Is there enough space to use the center alignment (which is preferred)?
        var halfElementHeight = _this4.element.clientHeight / 2;

        if (activatorBoundingRect.top >= halfElementHeight && windowHeight - activatorBoundingRect.bottom >= halfElementHeight) {
          alignment = 'center';
        } else if (windowHeight - activatorBoundingRect.bottom >= halfElementHeight) {
          alignment = 'bottom';
        } else {
          alignment = 'top';
        }
      }

      if (position === 'top') {
        topPosition = activatorBoundingRect.top - _this4.element.clientHeight - threshold;
        rightPosition = window.innerWidth - activatorBoundingRect.right;
      } else if (position === 'bottom') {
        topPosition = activatorBoundingRect.bottom + threshold;
        rightPosition = window.innerWidth - activatorBoundingRect.right;
      } else {
        rightPosition = window.innerWidth - activatorBoundingRect.left + threshold;

        if (alignment === 'center') {
          topPosition = activatorBoundingRect.top - _this4.element.clientHeight / 2 + _this4.activator.clientHeight / 2;
        } else if (alignment === 'top') {
          topPosition = activatorBoundingRect.bottom - _this4.element.clientHeight;
        } else {
          topPosition = activatorBoundingRect.top;
        }
      }
    });

    fastdom.mutate(function () {
      ['popover--positionBottom', 'popover--positionTop', 'popover--positionCenter', 'popover--alignTop', 'popover--alignCenter', 'popover--alignBottom'].map(function (item) {
        return _this4.element.classList.remove(item);
      });
      _this4.element.classList.add('popover--position' + (position.charAt(0).toUpperCase() + position.slice(1)));
      _this4.element.classList.add('popover--align' + (alignment.charAt(0).toUpperCase() + alignment.slice(1)));

      _this4.element.setAttribute('style', 'top: ' + parseInt(topPosition) + 'px; right: ' + parseInt(rightPosition) + 'px;');
    });
  }

  /**
   * Handle a11y events
   */

  _handleKeyboard(event) {
    if (this.isOpen && event.keyCode === 27) {
      this.close();
    }
  }
}